/**
 * The named prompt fields an agent is built from. Template content for these
 * fields is platform IP (super-admin only). A merchant overrides each field
 * with a mode — but never sees the underlying template text in inherit/augment
 * mode (that resolution happens server-side, in {@link resolveField}).
 */
export const AGENT_PROMPT_FIELDS = [
  'core_guardrails',
  'playbook',
  'qualifying_questions',
  'terminology_synonyms',
  'tone',
] as const;

export type AgentPromptField = (typeof AGENT_PROMPT_FIELDS)[number];

export function isAgentPromptField(value: string): value is AgentPromptField {
  return (AGENT_PROMPT_FIELDS as readonly string[]).includes(value);
}

/** How a merchant override combines with the platform template field. */
export type OverrideMode = 'inherit' | 'augment' | 'overwrite';

export const OVERRIDE_MODES: readonly OverrideMode[] = ['inherit', 'augment', 'overwrite'];

/** Merchant-friendly labels + helper copy for the agent editor. */
export const FIELD_META: Record<AgentPromptField, { label: string; hint: string }> = {
  core_guardrails: {
    label: 'Core guardrails',
    hint: 'Safety and sales fundamentals. Usually left inherited.',
  },
  playbook: {
    label: 'Sales playbook',
    hint: 'The qualify → decide → present flow. Hard constraints and no-match trade-offs stay enforced by the platform.',
  },
  qualifying_questions: {
    label: 'Qualifying questions',
    hint: 'Questions to draw from only when one missing answer would materially change the ranking.',
  },
  terminology_synonyms: {
    label: 'Terminology & synonyms',
    hint: 'Category jargon and feature synonyms.',
  },
  tone: {
    label: 'Tone of voice',
    hint: 'How the agent should sound. Most merchants customise this.',
  },
};

export type PersonaArchetype = 'concierge' | 'practical';

/** Short persona flavour appended to the compiled prompt. Part of the IP. */
export const PERSONA_FLAVOUR: Record<PersonaArchetype, string> = {
  concierge:
    'Adopt a Concierge persona: refined, unhurried, and consultative. Emphasise design, integration, materials, and lifestyle fit over price.',
  practical:
    'Adopt a Practical Salesperson persona: direct, efficient, and value-focused. Emphasise price, availability, dimensions, and everyday utility.',
};
